/**
 * Fil de discussion attaché à un rapport journalier :
 *  - Admin/super_admin peut commenter, créer des actions à mener, répondre.
 *  - Auteur du rapport peut commenter / répondre / marquer les actions comme faites.
 *
 * Les messages sont threadés (parent facultatif).
 * Les actions à mener ont un statut (à faire / en cours / faite / abandonnée)
 * et une deadline optionnelle.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  MessageSquare, Send, CornerDownRight, Edit3, Trash2, Check,
  ClipboardList, Clock, X, Loader2, AlertTriangle,
} from 'lucide-react';
import {
  useListCommentairesRapportQuery,
  useAjouterCommentaireRapportMutation,
  useMarquerCommentairesLusMutation,
  useModifierCommentaireRapportMutation,
  useSupprimerCommentaireRapportMutation,
  useChangerStatutActionMutation,
  type CommentaireRapport,
  type StatutAction,
} from '../../services/serviceScolariteApi';

interface Props {
  rapportId: number;
  auteurRapportId: number;
  utilisateurCourantId: number;
  roleCourant: string; // 'admin' | 'super_admin' | 'agent' | ...
  compact?: boolean;   // Mode compact (pour cartes historique)
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  if (sameDay) return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleString('fr-FR', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

const STATUT_LABELS: Record<Exclude<StatutAction, ''>, { label: string; color: string; bg: string }> = {
  a_faire: { label: 'À faire', color: 'text-amber-700 dark:text-amber-300', bg: 'bg-amber-100 dark:bg-amber-900/30' },
  en_cours: { label: 'En cours', color: 'text-blue-700 dark:text-blue-300', bg: 'bg-blue-100 dark:bg-blue-900/30' },
  faite: { label: 'Faite', color: 'text-emerald-700 dark:text-emerald-300', bg: 'bg-emerald-100 dark:bg-emerald-900/30' },
  abandonnee: { label: 'Abandonnée', color: 'text-gray-500 dark:text-gray-400', bg: 'bg-gray-100 dark:bg-gray-800' },
};

export const FilCommentairesRapport: React.FC<Props> = ({
  rapportId, auteurRapportId, utilisateurCourantId, roleCourant, compact,
}) => {
  const { data: messages = [], isFetching, refetch } = useListCommentairesRapportQuery(rapportId);
  const [ajouter, { isLoading: sending }] = useAjouterCommentaireRapportMutation();
  const [marquerLus] = useMarquerCommentairesLusMutation();
  const [modifier] = useModifierCommentaireRapportMutation();
  const [supprimer] = useSupprimerCommentaireRapportMutation();
  const [changerStatut] = useChangerStatutActionMutation();

  const estAuteur = utilisateurCourantId === auteurRapportId;
  const estAdmin = roleCourant === 'admin' || roleCourant === 'super_admin';

  const [nouveauTexte, setNouveauTexte] = useState('');
  const [nouveauType, setNouveauType] = useState<'commentaire' | 'action' | 'reponse_agent'>('commentaire');
  const [nouveauDeadline, setNouveauDeadline] = useState('');
  const [parentId, setParentId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');

  // Marquer les messages comme lus quand on affiche le fil (une fois monté avec des messages)
  useEffect(() => {
    if (!messages.length) return;
    const jamaisLus = messages.some(m =>
      (estAuteur && !m.lu_par_auteur_rapport) || (estAdmin && !m.lu_par_admin));
    if (jamaisLus) {
      marquerLus(rapportId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]);

  // Regroupement : messages racine + réponses
  const arbre = useMemo(() => {
    const racines = messages.filter(m => !m.parent);
    const reponses = new Map<number, CommentaireRapport[]>();
    messages.filter(m => m.parent !== null).forEach(m => {
      const list = reponses.get(m.parent!) || [];
      list.push(m);
      reponses.set(m.parent!, list);
    });
    return { racines, reponses };
  }, [messages]);

  const soumettre = async () => {
    const contenu = nouveauTexte.trim();
    if (!contenu) return;
    try {
      const body: any = {
        rapportId,
        contenu,
        type_message: nouveauType,
      };
      if (parentId) body.parent = parentId;
      if (nouveauType === 'action' && nouveauDeadline) body.deadline_action = nouveauDeadline;
      await ajouter(body).unwrap();
      setNouveauTexte(''); setNouveauDeadline(''); setParentId(null);
      setNouveauType(estAdmin ? 'commentaire' : 'commentaire');
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur lors de l\'envoi');
    }
  };

  const soumettreEdition = async (id: number) => {
    const contenu = editingText.trim();
    if (!contenu) return;
    try {
      await modifier({ id, contenu, rapportId }).unwrap();
      setEditingId(null); setEditingText('');
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    }
  };

  const rendreMessage = (m: CommentaireRapport, estReponse = false) => {
    const isAction = m.type_message === 'action';
    const isReponseAgent = m.type_message === 'reponse_agent';
    const auteurLabel = m.auteur_role === 'admin' || m.auteur_role === 'super_admin'
      ? 'Admin' : 'Agent';
    const statutInfo = isAction && m.statut_action && m.statut_action in STATUT_LABELS
      ? STATUT_LABELS[m.statut_action as Exclude<StatutAction, ''>]
      : null;
    const bulle = isAction
      ? 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800'
      : isReponseAgent
      ? 'bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800'
      : 'bg-gray-50 dark:bg-gray-800/60 border-gray-200 dark:border-gray-700';

    return (
      <div key={m.id}
        className={`${estReponse ? 'ml-6 mt-2' : ''} rounded border ${bulle} p-3`}>
        <div className="flex items-start gap-2 mb-1">
          <div className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
            {(m.auteur_nom || '?').charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <span className="font-semibold text-gray-900 dark:text-gray-100">{m.auteur_nom}</span>
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                m.auteur_role === 'admin' || m.auteur_role === 'super_admin'
                  ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}>{auteurLabel}</span>
              {isAction && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500 text-white">
                <ClipboardList size={10} /> Action
              </span>}
              {isReponseAgent && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-500 text-white">
                <CornerDownRight size={10} /> Réponse
              </span>}
              <span className="text-gray-500">{formatDateTime(m.date_creation)}</span>
              {m.modifie && <span className="text-gray-400 italic">(modifié)</span>}
            </div>
            {editingId === m.id ? (
              <div className="mt-1">
                <textarea value={editingText} onChange={e => setEditingText(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100" />
                <div className="flex gap-2 mt-1">
                  <button onClick={() => soumettreEdition(m.id)}
                    className="text-xs px-2 py-0.5 rounded bg-blue-600 text-white">Enregistrer</button>
                  <button onClick={() => { setEditingId(null); setEditingText(''); }}
                    className="text-xs px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">Annuler</button>
                </div>
              </div>
            ) : (
              <div className="mt-1 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">
                {m.contenu}
              </div>
            )}
            {isAction && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {statutInfo && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold ${statutInfo.bg} ${statutInfo.color}`}>
                    {m.statut_action === 'faite' ? <Check size={11} /> : <Clock size={11} />}
                    {statutInfo.label}
                  </span>
                )}
                {m.deadline_action && (
                  <span className="text-[11px] text-gray-600 dark:text-gray-400 inline-flex items-center gap-0.5">
                    <Clock size={11} /> Échéance : {new Date(m.deadline_action).toLocaleDateString('fr-FR')}
                  </span>
                )}
                {m.action_terminee_le && (
                  <span className="text-[11px] text-emerald-600 dark:text-emerald-400">
                    Terminée le {formatDateTime(m.action_terminee_le)}
                  </span>
                )}
                {/* Boutons changement de statut */}
                {(estAdmin || estAuteur) && m.statut_action !== 'faite' && m.statut_action !== 'abandonnee' && (
                  <div className="flex gap-1">
                    {m.statut_action === 'a_faire' && (
                      <button onClick={() => changerStatut({ id: m.id, statut_action: 'en_cours', rapportId })}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-blue-600 text-white hover:bg-blue-700">
                        Démarrer
                      </button>
                    )}
                    <button onClick={() => changerStatut({ id: m.id, statut_action: 'faite', rapportId })}
                      className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-0.5">
                      <Check size={10} /> Marquer faite
                    </button>
                    {estAdmin && (
                      <button onClick={() => {
                        if (window.confirm('Abandonner cette action ?')) {
                          changerStatut({ id: m.id, statut_action: 'abandonnee', rapportId });
                        }
                      }}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-gray-500 text-white hover:bg-gray-600">
                        Abandonner
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            {/* Actions transverses */}
            <div className="mt-1 flex items-center gap-3 text-[11px]">
              {!estReponse && (estAdmin || estAuteur) && (
                <button onClick={() => { setParentId(m.id); setNouveauType(estAdmin ? 'commentaire' : 'reponse_agent'); }}
                  className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-0.5">
                  <CornerDownRight size={10} /> Répondre
                </button>
              )}
              {m.peut_modifier && (
                <button onClick={() => { setEditingId(m.id); setEditingText(m.contenu); }}
                  className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 inline-flex items-center gap-0.5">
                  <Edit3 size={10} /> Modifier
                </button>
              )}
              {(m.peut_modifier || estAdmin) && (
                <button onClick={async () => {
                  if (!window.confirm('Supprimer ce message ?')) return;
                  try { await supprimer({ id: m.id, rapportId }).unwrap(); } catch (e: any) { alert(e?.data?.detail || 'Erreur'); }
                }}
                  className="text-red-500 hover:text-red-700 inline-flex items-center gap-0.5">
                  <Trash2 size={10} /> Supprimer
                </button>
              )}
            </div>
          </div>
        </div>
        {/* Réponses filles */}
        {arbre.reponses.get(m.id)?.map(r => rendreMessage(r, true))}
      </div>
    );
  };

  return (
    <div className={compact ? 'text-xs' : ''}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
          <MessageSquare size={14} />
          Fil de discussion
          {messages.length > 0 && <span className="text-xs font-normal text-gray-500">({messages.length})</span>}
        </h4>
        {isFetching && <Loader2 size={12} className="animate-spin text-gray-400" />}
      </div>

      {arbre.racines.length === 0 && !isFetching && (
        <div className="text-xs text-gray-500 dark:text-gray-400 italic mb-3">
          Aucun message. {estAdmin ? 'Ajoutez un commentaire ou une action à mener.' : 'En attente d\'un retour de l\'admin.'}
        </div>
      )}
      <div className="space-y-2 mb-3 max-h-[500px] overflow-y-auto pr-1">
        {arbre.racines.map(m => rendreMessage(m))}
      </div>

      {/* Zone de saisie */}
      {(estAdmin || estAuteur) && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3">
          {parentId && (
            <div className="mb-2 text-xs text-blue-600 dark:text-blue-400 inline-flex items-center gap-1 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded">
              <CornerDownRight size={11} /> Réponse à un message
              <button onClick={() => setParentId(null)} className="ml-1 hover:text-red-500"><X size={12} /></button>
            </div>
          )}
          {/* Sélecteur de type (admin uniquement pour actions) */}
          {estAdmin && !parentId && (
            <div className="flex flex-wrap items-center gap-2 mb-2 text-xs">
              <span className="text-gray-500">Type :</span>
              <button type="button" onClick={() => setNouveauType('commentaire')}
                className={`px-2 py-0.5 rounded ${nouveauType === 'commentaire'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200'}`}>
                Commentaire
              </button>
              <button type="button" onClick={() => setNouveauType('action')}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded ${nouveauType === 'action'
                  ? 'bg-amber-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200'}`}>
                <ClipboardList size={11} /> Action à mener
              </button>
              {nouveauType === 'action' && (
                <label className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-400 ml-2">
                  Échéance :
                  <input type="date" value={nouveauDeadline}
                    onChange={e => setNouveauDeadline(e.target.value)}
                    className="border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5 text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
                </label>
              )}
            </div>
          )}
          <textarea value={nouveauTexte} onChange={e => setNouveauTexte(e.target.value)}
            rows={3}
            placeholder={
              nouveauType === 'action'
                ? "Décrire l'action à mener (que doit faire l'agent en réponse à la difficulté ?)"
                : parentId ? 'Votre réponse…'
                : estAdmin ? 'Votre commentaire (l\'agent le verra dans son espace)…'
                : 'Votre message à l\'admin…'
            }
            className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 resize-y" />
          <div className="flex items-center justify-end gap-2 mt-2">
            {nouveauTexte && (
              <button onClick={() => { setNouveauTexte(''); setParentId(null); setNouveauDeadline(''); }}
                className="text-xs px-2 py-1 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800">
                Annuler
              </button>
            )}
            <button onClick={soumettre} disabled={!nouveauTexte.trim() || sending}
              className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-40">
              {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              Envoyer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilCommentairesRapport;
